import React from 'react';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { momentParseDate } from '../../../../helpers';
import { formatMoney } from '../../../../utils/utils';
import {
    ServiceManifestDataForm,
    ServiceManifestFilters
} from '../../../../app/models/ServiceManifest';
import ServiceManifestFilter from './ServiceManifestFilter';
import { Link } from 'react-router-dom';

interface Props {
    serviceManifests: ServiceManifestDataForm[];
    totalRows: number;
    loading?: boolean;
    edit?: (id: number) => void;
    show?: (id: number) => void;
    destroy?: (id: number) => void;
    filter: ServiceManifestFilters;
    setFilter: (filter: ServiceManifestFilters) => void;

    paginationRowsPerPageOptions?: any[];
    columnDict: string[]
}

const ServiceManifestDataTable = ({
    serviceManifests,
    totalRows,
    loading = false,
    edit,
    destroy,
    show,
    filter,
    setFilter,
    columnDict
}: Props) => {
    const columns = [
        {
            name: 'Id',
            selector: (row: any) => row?.id,
            sortable: true,
            sortField: 'id',
            width: '70px',
            omit: !columnDict.includes('Id'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>#{row?.id}</span>
            )
        },
        {
            name: 'RUT Sociedad',
            selector: (row: any) => row?.society_rut,
            sortable: false,
            width: '140px',
            omit: !columnDict.includes('RUT Sociedad'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{row.society_rut || '—'}</span>
            )
        },
        {
            name: 'Sociedad',
            selector: (row: any) => row?.society,
            sortable: true,
            sortField: 'society',
            width: '220px',
            omit: !columnDict.includes('Sociedad'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '215px' }}>
                    {row.society || '—'}
                </span>
            )
        },
        {
            name: 'RUT Proveedor',
            selector: (row: any) => row?.provider_rut,
            sortable: false,
            width: '140px',
            omit: !columnDict.includes('RUT Proveedor'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{row.provider_rut || '—'}</span>
            )
        },
        {
            name: 'Proveedor',
            selector: (row: any) => row?.provider,
            sortable: true,
            sortField: 'provider',
            width: '260px',
            omit: !columnDict.includes('Proveedor'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '255px' }}>
                    {row.provider || '—'}
                </span>
            )
        },
        {
            name: 'Orden de compra',
            selector: (row: any) => row?.purchase_order_id,
            sortable: true,
            sortField: 'purchase_order_id',
            width: '140px',
            omit: !columnDict.includes('Orden de compra'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ display: 'block', textAlign: 'center', width: '100%', whiteSpace: 'nowrap' }}>
                    <Link to={'/purchase-orders/' + row?.purchase_order_id + '/show'} style={{ color: 'black', textDecoration: 'underline' }}>
                        #{row?.purchase_order_id}
                    </Link>
                </span>
            )
        },
        {
            name: 'Fecha de entrega',
            selector: (row: any) => row?.delivery_date,
            sortable: true,
            sortField: 'delivery_date',
            width: '140px',
            omit: !columnDict.includes('Fecha de entrega'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{momentParseDate(row?.delivery_date)}</span>
            )
        },
        {
            name: 'Estado',
            selector: (row: any) => row?.status,
            sortable: true,
            sortField: 'status',
            width: '120px',
            omit: !columnDict.includes('Estado'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{row?.status}</span>
            )
        },
        {
            name: 'Total Neto',
            selector: (row: any) => row?.gross_total_calculated,
            sortable: false,
            width: '140px',
            omit: !columnDict.includes('Total Neto'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ display: 'block', textAlign: 'right', width: '100%', whiteSpace: 'nowrap' }}>
                    {row.gross_total_calculated !== null
                        ? formatMoney(row.gross_total_calculated ?? 0, row.currency, false)
                        : '—'}
                </span>
            )
        },
        {
            name: 'Moneda',
            selector: (row: any) => row?.currency,
            sortable: false,
            width: '90px',
            omit: !columnDict.includes('Moneda'),
            cell: (row: ServiceManifestDataForm) => (
                <span style={{ whiteSpace: 'nowrap' }}>{row.currency || '—'}</span>
            )
        },
        {
            name: 'Acciones',
            selector: (row: any) => row?.id,
            sortable: false,
            width: '140px',
            omit: !columnDict.includes('Acciones'),
            cell: (row: ServiceManifestDataForm) => (
                <div className="">
                    {show && (
                        <ButtonTableAction
                            callbackFunction={() => show(row?.id ?? -1)}
                            classIcon={'mdi mdi-eye'}
                            colorIcon={'text-info'}
                            errorMessage={'No se puede mostrar este registro.'}
                            title={'Ver'}
                        />
                    )}
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row?.id ?? -1)}
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            errorMessage={'No se puede editar este registro.'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row?.id ?? -1)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            errorMessage={'No se puede eliminar este registro.'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            <div className="row mt-3">
                <div className="col">
                    <ServiceManifestFilter 
                        filter={filter} 
                        setFilter={setFilter} 
                        columnDict={columnDict}
                    />
                </div>
            </div>

            <DefaultDataTable
                columns={columns}
                data={serviceManifests}
                progressPending={loading}
                paginationTotalRows={totalRows}
                filter={filter}
                setFilter={setFilter}
            />
        </>
    );
};

export default ServiceManifestDataTable;
